// Shared "Assign Role To User" modal.
//
// Used from both the Settings -> Billing -> User Roles screen and a staff
// profile's Security Roles tab. It owns the create dispatch, the success/error
// toast and the assignment-list refresh, so callers just render it and pass an
// optional `onAssigned` callback to refresh their own view.
//
// `preset` pre-selects a side ({ User_Id } or { SecurityAuthRole_Id }); pass
// `lockUser`/`lockRole` to freeze that dropdown (e.g. a staff profile always
// assigns to that one person).

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

import {
  createSecurityAuthRoleToUser,
  createSecurityAuthRoleToUserSuccess,
  getSecurityAuthRoleList,
  getSecurityAuthRoleToUserList,
} from "../../../store/security/action";
import { getStaffList } from "../../../store/staff/action";
import AppModal from "../../Common/AppModal";
import filterActiveStaff from "../../Common/staffActive";

const emptyForm = {
  User_Id: "",
  SecurityAuthRole_Id: "",
  StartDate: "",
  EndDate: "",
};

export default function AssignSecurityRole({
  open,
  toggle,
  preset,
  lockUser = false,
  lockRole = false,
  onAssigned,
}) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(emptyForm);

  // Reset the form to the caller's preset every time the modal opens.
  useEffect(() => {
    if (open) setForm({ ...emptyForm, ...preset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    dispatch(getStaffList());
    dispatch(getSecurityAuthRoleList());
  }, [dispatch]);

  const staff = useSelector((state) => state.Staff.staffList?.staff_list);
  const roles = useSelector(
    (state) => state.Security.securityAuthRoleList?.securityauthrole_list
  );
  const { createSuccess, createLoading } = useSelector((state) => ({
    createSuccess: state.Security.securityAuthRoleToUserCreateSuccess,
    createLoading: state.Security.securityAuthRoleToUserCreateLoading,
  }));
  // Task 24686 - existing assignments, so the picker can grey out anyone who
  // already holds the role being assigned.
  const assignments = useSelector(
    (state) =>
      state.Security.securityAuthRoleToUserList?.securityauthroletouser_list
  );

  // The list is needed before the modal can grey anything out. The User Roles
  // screen loads it, a staff profile may not, so ask for it here too.
  useEffect(() => {
    if (open) dispatch(getSecurityAuthRoleToUserList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Task 24686 - the user side accepts many people at once, so User_Id is held
  // as an array internally whatever the caller passed in. A staff profile still
  // passes a single preset and locks the field; that path stays single.
  const selectedUserIds = useMemo(() => {
    if (Array.isArray(form.User_Id)) return form.User_Id;
    return form.User_Id ? [form.User_Id] : [];
  }, [form.User_Id]);

  const multiUser = !lockUser;

  // Who already holds the role being assigned. Picking one person twice was
  // easy enough to spot; picking fifteen and duplicating three of them is not,
  // and nothing on the backend stops a second identical row being written.
  const alreadyAssignedUserIds = useMemo(() => {
    if (!form.SecurityAuthRole_Id) return [];
    return (assignments || [])
      .filter(
        (a) =>
          String(a?.SecurityAuthRole_Id) === String(form.SecurityAuthRole_Id)
      )
      .map((a) => String(a?.User_Id));
  }, [assignments, form.SecurityAuthRole_Id]);

  const staffOptions = useMemo(
    () =>
      filterActiveStaff(staff, selectedUserIds)
        .map((s) => ({
          value: s.Id,
          label: s.FullName,
          isDisabled: alreadyAssignedUserIds.includes(String(s.Id)),
        }))
        .map((o) =>
          o.isDisabled ? { ...o, label: `${o.label} — already has this role` } : o
        ),
    [staff, selectedUserIds, alreadyAssignedUserIds]
  );
  // Task 24686 follow-up - names for the "who you have added" row. Built from
  // the raw staff list, not staffOptions, whose labels carry the
  // "— already has this role" suffix that would read as nonsense on a chip.
  const selectedUserChips = useMemo(
    () =>
      selectedUserIds.map((id) => {
        const member = (staff || []).find(
          (s) => String(s.Id) === String(id)
        );
        return { value: id, label: member?.FullName || `#${id}` };
      }),
    [selectedUserIds, staff]
  );

  const roleOptions = useMemo(
    () => (roles || []).map((r) => ({ value: r.Id, label: r.Name })),
    [roles]
  );

  useEffect(() => {
    if (isEmpty(createSuccess)) return;
    if (createSuccess.status === 200) {
      // Task 24686 - the API's "Created Successfully" says nothing about how
      // many landed, which is the one thing you want to know after a batch.
      toast.success(
        selectedUserIds.length > 1
          ? `Role assigned to ${selectedUserIds.length} people`
          : createSuccess.message
      );
      dispatch(getSecurityAuthRoleToUserList());
      onAssigned?.();
      toggle?.();
    } else {
      toast.error(createSuccess.message);
    }
    dispatch(createSecurityAuthRoleToUserSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess]);

  const handleAssign = () => {
    if (!selectedUserIds.length || !form.SecurityAuthRole_Id) {
      toast.error("Please select both a user and a role.");
      return;
    }
    if (form.StartDate && form.EndDate && form.EndDate < form.StartDate) {
      toast.error("End date cannot be before the start date.");
      return;
    }
    // Task 24686 - one row per person. /securityauthroletouser/create already
    // loops the posted array, so the whole batch is a single request.
    dispatch(
      createSecurityAuthRoleToUser(
        selectedUserIds.map((userId) => ({
          User_Id: userId,
          SecurityAuthRole_Id: form.SecurityAuthRole_Id,
          StartDate: form.StartDate || null,
          EndDate: form.EndDate || null,
        }))
      )
    );
  };

  return (
    <AppModal
      open={open}
      onClose={toggle}
      title="Assign Role To User"
      icon={<SecurityOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={createLoading}
      onSubmit={handleAssign}
      submitLabel={
        selectedUserIds.length > 1
          ? `Assign to ${selectedUserIds.length} people`
          : "Assign"
      }
      submitColor="primary"
    >
      <div>
        <Label>
          {multiUser ? "Select Users" : "Select User"}
          {multiUser && selectedUserIds.length > 1 && (
            <span className="text-muted">
              {" "}
              — {selectedUserIds.length} selected
            </span>
          )}
        </Label>
        {/* Task 24686 follow-up - who has been added, shown ABOVE the control
            and outside it.
            react-select keeps its chips inside the control, and the menu -
            held open so you can add several people without reopening it -
            renders over that control inside the dialog. So the list of who
            you had just picked was hidden by the thing you were picking them
            with. This row cannot be covered: the menu opens downwards, this
            sits above it. */}
        {multiUser && selectedUserChips.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mb-2">
            {selectedUserChips.map((chip) => (
              <span
                key={chip.value}
                className="badge bg-primary-subtle text-primary border border-primary d-inline-flex align-items-center"
              >
                {chip.label}
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  style={{ fontSize: "0.5rem" }}
                  aria-label={`Remove ${chip.label}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      User_Id: selectedUserIds.filter(
                        (id) => String(id) !== String(chip.value)
                      ),
                    })
                  }
                />
              </span>
            ))}
          </div>
        )}
        {/* Task 24686 - pick as many people as you like and give them all the
            same role in one go. Locked callers (a staff profile, which always
            assigns to that one person) stay single-select. */}
        <Select
          options={staffOptions}
          isDisabled={lockUser}
          isMulti={multiUser}
          closeMenuOnSelect={!multiUser}
          /* Portalled out of the dialog so the menu is positioned against the
             viewport rather than squeezed inside a centred modal, which is
             what made it flip up over the control. zIndex clears MUI's
             Dialog. Same pattern as the venue picker in ReservationDiaryV2. */
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          menuPlacement="bottom"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 13000 }) }}
          placeholder={
            multiUser ? "Choose one or more people…" : "Choose a person…"
          }
          value={
            multiUser
              ? staffOptions.filter((o) => selectedUserIds.includes(o.value))
              : staffOptions.find((o) => o.value === selectedUserIds[0]) || null
          }
          onChange={(e) =>
            setForm({
              ...form,
              User_Id: multiUser ? (e || []).map((o) => o.value) : e?.value,
            })
          }
        />
        {multiUser && !!form.SecurityAuthRole_Id && (
          <small className="text-muted">
            Anyone already holding this role is greyed out.
          </small>
        )}
      </div>
      <div className="pt-2">
        <Label>Select Role</Label>
        <Select
          options={roleOptions}
          isDisabled={lockRole}
          value={
            roleOptions.find((o) => o.value === form.SecurityAuthRole_Id) ||
            null
          }
          onChange={(e) => setForm({ ...form, SecurityAuthRole_Id: e?.value })}
        />
      </div>
      <Row className="pt-2 g-2">
        <Col xs={6}>
          <Label>Start Date</Label>
          <Input
            type="date"
            value={form.StartDate}
            onChange={(e) => setForm({ ...form, StartDate: e.target.value })}
          />
        </Col>
        <Col xs={6}>
          <Label>
            End Date <span className="text-muted">(blank = forever)</span>
          </Label>
          <Input
            type="date"
            value={form.EndDate}
            min={form.StartDate || undefined}
            onChange={(e) => setForm({ ...form, EndDate: e.target.value })}
          />
        </Col>
      </Row>
    </AppModal>
  );
}
